import React, { useCallback, useEffect, useState } from 'react';
import { apiErrorMessage } from '../../utils/apiError';
import mcpServiceService from '../../services/mcpServiceService';
import Button from '../shared/Button';
import ConfirmModal from '../shared/ConfirmModal';

// HCONNECT'S DEFAULT MCP AS A SERVICE AGREEMENT — superadmin (V641, doc/MCP-AS-A-SERVICE.md §9.2). The platform's own
// side of the agreement: draft and publish the default every hotel accepts, and approve the versions hotels propose.
// It is one platform-wide artefact, so it lives on Admin › Platform › MCP as a Service (McpAdminPage), not on any
// hotel's Terms tab — a change here asks every hotel on the default to accept again.
//
// GET/POST /terms/default and POST /terms/{id}/publish answer superadmins only (404 for anyone else).
//
// `section` picks what is drawn — 'proposals' (the hotels' proposals waiting for approval) or 'default' (the default
// agreement and its versions) — so each can be its own tab on the admin page; both read the same GET /terms/default.

const when = (v) => (v ? new Date(v).toLocaleString() : '—');

export default function McpDefaultAgreement({ section = 'default' }) {
  const [def, setDef] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [editor, setEditor] = useState(null);   // { title, body, publish }

  const load = useCallback(async () => {
    setError(null);
    try { setDef(await mcpServiceService.getDefaultAgreement()); } catch (e) { setError(apiErrorMessage(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (what, fn) => {
    setBusy(what); setError(null);
    try { await fn(); await load(); } catch (e) { setError(apiErrorMessage(e)); } finally { setBusy(null); }
  };
  const saveEditor = () => {
    const e = editor;
    setEditor(null);
    return act('default', () => mcpServiceService.saveDefaultAgreement({ title: e.title, body: e.body, publish: !!e.publish }));
  };

  return (
    <div className="mcps-section">
      {error && <div className="os-msg os-msg-error">{error}</div>}
      {!def && !error && <p>Loading…</p>}

      {def && (
        <>
          {section === 'proposals' && <div className="mcps-card">
            <div className="mcps-card-head"><h3>Proposals waiting for approval</h3></div>
            {def.proposals.length ? (
              <table className="mcps-table"><thead><tr><th>Hotel</th><th>Proposal</th><th>By</th><th></th></tr></thead>
                <tbody>{def.proposals.map(p => (
                  <tr key={p.id}><td>{p.hotel_name}</td><td>{p.title} (v{p.version})</td><td>{p.created_by} · {when(p.created_at)}</td>
                    <td><Button variant="primary" disabled={!!busy} onClick={() => act(`approve-${p.id}`, () => mcpServiceService.publishTerms(p.id))}>Approve</Button></td></tr>))}</tbody></table>
            ) : <p className="mcps-meta">No hotel has proposed its own agreement.</p>}
            <p className="mcps-meta">An approved proposal becomes that hotel&rsquo;s agreement; the hotel&rsquo;s admin must then accept it.</p>
          </div>}

          {section === 'default' && <div className="mcps-card">
            <div className="mcps-card-head"><h3>Default agreement</h3>
              {def.current && <span className="mcps-meta">current: version {def.current.version}</span>}</div>
            {def.current ? <div className="mcps-terms-text">{def.current.body}</div>
              : <p className="mcps-meta">Not published yet — no hotel can go live until it is, and its admin has accepted it.</p>}
            {def.history.length > 0 && (
              <table className="mcps-table"><thead><tr><th>Version</th><th>Title</th><th>Status</th><th>Published</th><th></th></tr></thead>
                <tbody>{def.history.map(h => (
                  <tr key={h.id}><td>{h.version}</td><td>{h.title}</td><td>{h.status}</td><td>{when(h.published_at)}</td>
                    <td>{h.status === 'draft' && <Button variant="primary" disabled={!!busy} onClick={() => act(`pub-${h.id}`, () => mcpServiceService.publishTerms(h.id))}>Publish</Button>}</td></tr>))}</tbody></table>
            )}
            <div className="mcps-actions">
              <Button variant="secondary" disabled={!!busy} onClick={() => setEditor({
                title: def.current ? def.current.title : 'hconnect MCP as a Service agreement', body: def.current ? def.current.body : '' })}>
                New version</Button>
            </div>
            <p className="mcps-meta">Publishing a new default asks every hotel on the default to accept it again before assistants can use it.</p>
          </div>}
        </>
      )}

      {editor && (
        <ConfirmModal wide closeOnBackdrop={false} title="Default agreement"
                      confirmLabel={editor.publish ? 'Publish' : 'Save draft'}
                      confirmDisabled={!editor.title.trim() || !editor.body.trim()}
                      onCancel={() => setEditor(null)} onConfirm={saveEditor}>
          <label className="mcps-wide">Title<input value={editor.title} maxLength={200} onChange={e => setEditor(x => ({ ...x, title: e.target.value }))} /></label>
          <label className="mcps-wide">Text<textarea rows={18} value={editor.body} onChange={e => setEditor(x => ({ ...x, body: e.target.value }))} /></label>
          <label className="mcps-check"><input type="checkbox" checked={!!editor.publish}
            onChange={e => setEditor(x => ({ ...x, publish: e.target.checked }))} /> Publish now (otherwise saved as a draft)</label>
        </ConfirmModal>
      )}
    </div>
  );
}
